
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

interface PaymentProcessorProps {
  orderId: string;
  orderTotal: number;
  customerName: string;
  onPaymentComplete: (paymentData: any) => void;
  onClose: () => void;
}

export default function PaymentProcessor({ 
  orderId, 
  orderTotal, 
  customerName, 
  onPaymentComplete, 
  onClose 
}: PaymentProcessorProps) {
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'qris'>('cash');
  const [cashReceived, setCashReceived] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);

  // Perbaikan validasi dan konversi orderTotal yang lebih robust
  const safeOrderTotal = React.useMemo(() => {
    try {
      let numTotal = 0;
      
      // Validasi input kosong atau null/undefined
      if (orderTotal === null || orderTotal === undefined) {
        console.warn('PaymentProcessor: orderTotal is null or undefined');
        return 0;
      }
      
      // Jika sudah berupa number yang valid
      if (typeof orderTotal === 'number') {
        if (isNaN(orderTotal) || !isFinite(orderTotal)) {
          console.warn('PaymentProcessor: orderTotal is NaN or infinite number');
          return 0;
        }
        numTotal = orderTotal;
      } 
      // Jika berupa string, konversi dengan validasi
      else if (typeof orderTotal === 'string') {
        // Bersihkan string dari karakter non-numeric kecuali titik dan koma
        const cleanedString = orderTotal.replace(/[^\d.,]/g, '').replace(',', '.');
        
        if (cleanedString === '' || cleanedString === '.') {
          console.warn('PaymentProcessor: orderTotal string is empty or invalid');
          return 0;
        }
        
        numTotal = parseFloat(cleanedString);
        
        if (isNaN(numTotal) || !isFinite(numTotal)) {
          console.warn('PaymentProcessor: failed to parse orderTotal string to number');
          return 0;
        }
      }
      // Fallback untuk tipe data lain
      else {
        const converted = Number(orderTotal);
        if (isNaN(converted) || !isFinite(converted)) {
          console.warn('PaymentProcessor: failed to convert orderTotal to number');
          return 0;
        }
        numTotal = converted;
      }
      
      // Validasi final - pastikan nilai positif dan masuk akal
      if (numTotal <= 0) {
        console.warn('PaymentProcessor: orderTotal is zero or negative');
        return 0;
      }
      
      // Validasi maksimum (mencegah nilai yang tidak masuk akal)
      if (numTotal > 100000000) { // 100 juta
        console.warn('PaymentProcessor: orderTotal exceeds maximum limit');
        return 0;
      }
      
      return Math.round(numTotal); // Bulatkan ke angka bulat terdekat
      
    } catch (error) {
      console.error('PaymentProcessor: Error processing orderTotal:', error);
      return 0;
    }
  }, [orderTotal]);

  // Debug log yang lebih informatif
  React.useEffect(() => {
    console.log('PaymentProcessor initialized with:', {
      orderId: orderId || 'MISSING',
      originalOrderTotal: orderTotal,
      orderTotalType: typeof orderTotal,
      processedOrderTotal: safeOrderTotal,
      customerName: customerName || 'MISSING',
      isValidOrder: orderId && safeOrderTotal > 0
    });
  }, [orderId, orderTotal, customerName, safeOrderTotal]);

  // Perbaikan function getChange dengan error handling
  const getChange = React.useCallback(() => {
    try {
      const received = parseFloat(cashReceived) || 0;
      const change = received - safeOrderTotal;
      return Math.max(0, Math.round(change));
    } catch (error) {
      console.error('Error calculating change:', error);
      return 0;
    }
  }, [cashReceived, safeOrderTotal]);

  // Perbaikan handleCashPayment dengan better error handling
  const handleCashPayment = async () => {
    try {
      setIsProcessing(true);
      
      const received = parseFloat(cashReceived) || 0;
      
      // Validasi input
      if (received <= 0) {
        alert('⚠️ Masukkan jumlah uang yang valid!');
        return;
      }
      
      if (received < safeOrderTotal) {
        const shortage = safeOrderTotal - received;
        alert(`⚠️ Uang kurang Rp ${shortage.toLocaleString('id-ID')}!\n\nTotal: Rp ${safeOrderTotal.toLocaleString('id-ID')}\nDiterima: Rp ${received.toLocaleString('id-ID')}`);
        return;
      }

      // Simulate payment processing dengan timeout yang lebih pendek
      await new Promise(resolve => setTimeout(resolve, 1000));

      const payment = {
        orderId: orderId || `ORDER-${Date.now()}`,
        method: 'cash',
        amount: safeOrderTotal,
        received: received,
        change: getChange(),
        timestamp: new Date().toISOString(),
        customerName: customerName || 'Guest',
        receiptNumber: `RCP-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`
      };

      setPaymentData(payment);
      setPaymentCompleted(true);
      
      // Call parent callback
      if (typeof onPaymentComplete === 'function') {
        onPaymentComplete(payment);
      }

      alert(`✅ Pembayaran Cash Berhasil!\n\nTotal: Rp ${safeOrderTotal.toLocaleString('id-ID')}\nDiterima: Rp ${received.toLocaleString('id-ID')}\nKembalian: Rp ${getChange().toLocaleString('id-ID')}\n\nNo. Struk: ${payment.receiptNumber}`);

    } catch (error) {
      console.error('Cash payment error:', error);
      alert('❌ Gagal memproses pembayaran cash.\n\nSilakan coba lagi atau hubungi admin jika masalah berlanjut.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Perbaikan handleCardPayment
  const handleCardPayment = async () => {
    try {
      setIsProcessing(true);

      // Simulate card payment processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      const payment = {
        orderId: orderId || `ORDER-${Date.now()}`,
        method: 'card',
        amount: safeOrderTotal,
        timestamp: new Date().toISOString(),
        customerName: customerName || 'Guest',
        receiptNumber: `RCP-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
        cardType: 'Debit/Credit Card'
      };

      setPaymentData(payment);
      setPaymentCompleted(true);
      
      if (typeof onPaymentComplete === 'function') {
        onPaymentComplete(payment);
      }

      alert(`✅ Pembayaran Kartu Berhasil!\n\nTotal: Rp ${safeOrderTotal.toLocaleString('id-ID')}\nMetode: Kartu Debit/Credit\nNo. Struk: ${payment.receiptNumber}\n\nTransaksi telah disetujui.`);

    } catch (error) {
      console.error('Card payment error:', error);
      alert('❌ Gagal memproses pembayaran kartu.\n\nPastikan kartu terbaca dengan baik dan coba lagi.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Perbaikan handleQRISPayment
  const handleQRISPayment = async () => {
    try {
      setIsProcessing(true);

      // Simulate QRIS payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      const payment = {
        orderId: orderId || `ORDER-${Date.now()}`,
        method: 'qris',
        amount: safeOrderTotal,
        timestamp: new Date().toISOString(),
        customerName: customerName || 'Guest',
        receiptNumber: `RCP-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
        qrisProvider: 'QRIS Payment'
      };

      setPaymentData(payment);
      setPaymentCompleted(true);
      
      if (typeof onPaymentComplete === 'function') {
        onPaymentComplete(payment);
      }

      alert(`✅ Pembayaran QRIS Berhasil!\n\nTotal: Rp ${safeOrderTotal.toLocaleString('id-ID')}\nMetode: QRIS\nNo. Struk: ${payment.receiptNumber}\n\nPembayaran telah dikonfirmasi.`);

    } catch (error) {
      console.error('QRIS payment error:', error);
      alert('❌ Gagal memproses pembayaran QRIS.\n\nPastikan QR code sudah dipindai dengan benar dan coba lagi.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Perbaikan printInvoice dengan error handling
  const printInvoice = React.useCallback(() => {
    try {
      if (!paymentData) {
        alert('⚠️ Data pembayaran tidak tersedia untuk dicetak!');
        return;
      }

      const invoiceContent = `
===========================================
           TAKEYA JAPANESE RESTAURANT
===========================================
Alamat: Jl. Sakura No. 123, Jakarta
Telp: (021) 1234-5678
Email: info@takeya.restaurant

-------------------------------------------
STRUK PEMBAYARAN
-------------------------------------------
No. Struk    : ${paymentData.receiptNumber || 'N/A'}
Tanggal      : ${new Date(paymentData.timestamp).toLocaleDateString('id-ID')}
Waktu        : ${new Date(paymentData.timestamp).toLocaleTimeString('id-ID')}
Kasir        : Admin User
Customer     : ${paymentData.customerName || 'Guest'}

-------------------------------------------
DETAIL PESANAN
-------------------------------------------
Order ID     : ${paymentData.orderId || 'N/A'}
Total        : Rp ${(paymentData.amount || 0).toLocaleString('id-ID')}

-------------------------------------------
PEMBAYARAN
-------------------------------------------
Metode       : ${paymentData.method === 'cash' ? 'Cash' : 
                paymentData.method === 'card' ? 'Kartu Debit/Credit' : 'QRIS'}
${paymentData.method === 'cash' && paymentData.received ? `
Diterima     : Rp ${paymentData.received.toLocaleString('id-ID')}
Kembalian    : Rp ${(paymentData.change || 0).toLocaleString('id-ID')}` : ''}

-------------------------------------------
         TERIMA KASIH ATAS KUNJUNGAN
            ANDA DI TAKEYA RESTAURANT!
===========================================
      `;

      // Improved print functionality
      const printWindow = window.open('', '_blank', 'width=400,height=600');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Invoice - ${paymentData.receiptNumber || 'Receipt'}</title>
              <style>
                body { 
                  font-family: 'Courier New', monospace; 
                  font-size: 12px; 
                  line-height: 1.4;
                  margin: 20px;
                  white-space: pre-line;
                  background: white;
                  color: black;
                }
                @media print {
                  body { margin: 0; }
                  @page { margin: 10mm; }
                }
              </style>
            </head>
            <body onload="window.print(); setTimeout(() => window.close(), 1000);">
              ${invoiceContent}
            </body>
          </html>
        `);
        printWindow.document.close();
        
        // Show success message
        setTimeout(() => {
          alert('✅ Struk berhasil dicetak!');
        }, 500);
      } else {
        // Fallback jika popup diblokir
        alert('⚠️ Popup diblokir oleh browser.\n\nSilakan aktifkan popup untuk browser ini atau salin data struk:\n\n' + invoiceContent);
      }
    } catch (error) {
      console.error('Print invoice error:', error);
      alert('❌ Gagal mencetak struk.\n\nSilakan coba lagi atau hubungi admin.');
    }
  }, [paymentData]);

  // Perbaikan handleClosePaymentModal
  const handleClosePaymentModal = React.useCallback(() => {
    try {
      if (paymentCompleted) {
        // Jika pembayaran sudah selesai, langsung tutup
        if (typeof onClose === 'function') {
          onClose();
        }
      } else if (isProcessing) {
        // Jika sedang memproses, tidak bisa ditutup
        alert('⏳ Sedang memproses pembayaran.\n\nMohon tunggu hingga proses selesai.');
        return;
      } else {
        // Konfirmasi jika belum selesai pembayaran
        const confirmed = confirm(
          '❗ Batalkan Proses Pembayaran?\n\n' +
          'Pembayaran yang sedang berlangsung akan dibatalkan dan pesanan akan tetap dalam status "Belum Bayar".\n\n' +
          'Yakin ingin menutup jendela pembayaran?'
        );
        
        if (confirmed) {
          // Reset payment states
          setPaymentCompleted(false);
          setPaymentData(null);
          setCashReceived('');
          setPaymentMethod('cash');
          
          if (typeof onClose === 'function') {
            onClose();
          }
          
          // Show cancellation notification
          try {
            const notification = document.createElement('div');
            notification.className = 'fixed top-4 right-4 bg-orange-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
            notification.innerHTML = `
              <i class="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
              <span>Proses pembayaran dibatalkan</span>
            `;
            document.body.appendChild(notification);
            
            setTimeout(() => {
              if (document.body.contains(notification)) {
                document.body.removeChild(notification);
              }
            }, 3000);
          } catch (notifError) {
            console.warn('Failed to show notification:', notifError);
          }
        }
      }
    } catch (error) {
      console.error('Error closing payment modal:', error);
      // Fallback: tutup modal langsung jika ada error
      if (typeof onClose === 'function') {
        onClose();
      }
    }
  }, [paymentCompleted, isProcessing, onClose]);

  // Perbaikan handleRefreshPage
  const handleRefreshPage = React.useCallback(() => {
    try {
      const confirmed = confirm(
        '🔄 Refresh Halaman\n\n' +
        'Halaman akan dimuat ulang untuk memperbaiki masalah data. ' +
        'Semua data yang belum disimpan akan hilang.\n\n' +
        'Lanjutkan refresh halaman?'
      );
      
      if (confirmed) {
        // Show loading notification
        try {
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
          notification.innerHTML = `
            <i class="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
            <span>Memuat ulang halaman...</span>
          `;
          document.body.appendChild(notification);
        } catch (notifError) {
          console.warn('Failed to show loading notification:', notifError);
        }
        
        // Refresh page after short delay
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Error refreshing page:', error);
      // Fallback: refresh langsung
      window.location.reload();
    }
  }, []);

  // Validasi cash input dengan real-time formatting
  const handleCashInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = e.target.value;
      
      // Hanya izinkan angka dan titik
      const cleanedValue = value.replace(/[^\d.]/g, '');
      
      // Cegah multiple titik
      const parts = cleanedValue.split('.');
      if (parts.length > 2) {
        return; // Ignore input jika ada lebih dari satu titik
      }
      
      // Batasi maksimum input (100 juta)
      const numValue = parseFloat(cleanedValue) || 0;
      if (numValue > 100000000) {
        return; // Ignore input jika melebihi batas
      }
      
      setCashReceived(cleanedValue);
    } catch (error) {
      console.error('Error handling cash input:', error);
    }
  }, []);

  // Jika orderTotal tidak valid, tampilkan error UI yang diperbaiki
  if (safeOrderTotal <= 0) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 max-w-lg w-full">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">Error Data Pesanan</h3>
            <div className="text-gray-300 mb-6 space-y-2">
              <p>Total pesanan tidak valid atau kosong.</p>
              <p className="text-sm text-red-400">
                Data: "{String(orderTotal)}" (Tipe: {typeof orderTotal})
              </p>
              <p className="text-sm text-gray-400">
                Silakan tutup jendela ini dan coba proses pesanan ulang, atau refresh halaman jika masalah berlanjut.
              </p>
            </div>
            <div className="space-y-3">
              <Button
                variant="primary"
                size="lg"
                onClick={handleRefreshPage}
                className="w-full whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                Refresh Halaman
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  try {
                    if (typeof onClose === 'function') {
                      onClose();
                    }
                  } catch (error) {
                    console.error('Error closing modal:', error);
                    window.location.reload();
                  }
                }}
                className="w-full whitespace-nowrap cursor-pointer border-gray-500 text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                <i className="ri-close-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                Tutup
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-red-500/20 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-white">Proses Pembayaran</h3>
            <button
              onClick={handleClosePaymentModal}
              disabled={isProcessing}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                isProcessing 
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
              title={isProcessing ? 'Sedang memproses...' : 'Tutup'}
            >
              <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>

          {/* Order Summary - Diperbaiki */}
          <div className="bg-gray-900/50 rounded-2xl p-4 mb-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300">Order ID:</span>
              <span className="text-white font-bold">{orderId || 'N/A'}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-300">Customer:</span>
              <span className="text-white font-semibold">{customerName || 'Guest'}</span>
            </div>
            <div className="flex items-center justify-between text-xl">
              <span className="text-white font-bold">Total:</span>
              <span className="text-yellow-400 font-bold">Rp {safeOrderTotal.toLocaleString('id-ID')}</span>
            </div>
          </div>

          {!paymentCompleted ? (
            <>
              {/* Payment Method Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-bold text-white mb-4">Pilih Metode Pembayaran</h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    disabled={isProcessing}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      paymentMethod === 'cash'
                        ? 'border-red-500 bg-red-500/20 text-white'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-red-400'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <i className="ri-money-dollar-circle-line text-2xl mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <div className="text-sm font-semibold">Cash</div>
                  </button>
                  
                  <button
                    onClick={() => setPaymentMethod('card')}
                    disabled={isProcessing}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      paymentMethod === 'card'
                        ? 'border-red-500 bg-red-500/20 text-white'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-red-400'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <i className="ri-bank-card-line text-2xl mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <div className="text-sm font-semibold">Kartu</div>
                  </button>
                  
                  <button
                    onClick={() => setPaymentMethod('qris')}
                    disabled={isProcessing}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      paymentMethod === 'qris'
                        ? 'border-red-500 bg-red-500/20 text-white'
                        : 'border-gray-600 bg-gray-800/50 text-gray-300 hover:border-red-400'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <i className="ri-qr-code-line text-2xl mb-2 w-8 h-8 flex items-center justify-center mx-auto"></i>
                    <div className="text-sm font-semibold">QRIS</div>
                  </button>
                </div>
              </div>

              {/* Cash Payment Input - Diperbaiki */}
              {paymentMethod === 'cash' && (
                <div className="mb-6">
                  <label className="block text-white font-semibold mb-2">
                    Jumlah Uang Diterima (Rp) *
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={cashReceived}
                    onChange={handleCashInputChange}
                    placeholder="0"
                    disabled={isProcessing}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-red-500 focus:outline-none text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  
                  {cashReceived && parseFloat(cashReceived) >= safeOrderTotal && (
                    <div className="mt-3 p-3 bg-green-500/20 rounded-xl border border-green-500/50">
                      <div className="flex justify-between items-center">
                        <span className="text-green-400 font-semibold">Kembalian:</span>
                        <span className="text-green-400 font-bold text-lg">
                          Rp {getChange().toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {cashReceived && parseFloat(cashReceived) > 0 && parseFloat(cashReceived) < safeOrderTotal && (
                    <div className="mt-3 p-3 bg-red-500/20 rounded-xl border border-red-500/50">
                      <span className="text-red-400 font-semibold">
                        Uang kurang Rp {(safeOrderTotal - parseFloat(cashReceived)).toLocaleString('id-ID')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Quick Cash Buttons - Diperbaiki */}
              {paymentMethod === 'cash' && !isProcessing && (
                <div className="mb-6">
                  <h5 className="text-white font-semibold mb-3">Nominal Cepat:</h5>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      safeOrderTotal,
                      Math.ceil(safeOrderTotal / 50000) * 50000,
                      Math.ceil(safeOrderTotal / 100000) * 100000,
                      safeOrderTotal + 25000,
                      safeOrderTotal + 50000,
                      safeOrderTotal + 100000
                    ].filter((amount, index, arr) => arr.indexOf(amount) === index && amount <= 10000000) // Remove duplicates dan batasi max 10 juta
                    .slice(0, 6) // Maksimal 6 button
                    .map((amount, index) => (
                      <button
                        key={index}
                        onClick={() => setCashReceived(amount.toString())}
                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {amount.toLocaleString('id-ID')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment Action Buttons - Diperbaiki */}
              <div className="space-y-3">
                {paymentMethod === 'cash' && (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCashPayment}
                    disabled={isProcessing || !cashReceived || parseFloat(cashReceived) <= 0 || parseFloat(cashReceived) < safeOrderTotal}
                    className="w-full whitespace-nowrap cursor-pointer"
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line mr-2 w-5 h-5 flex items-center justify-center animate-spin"></i>
                        Memproses Cash...
                      </>
                    ) : (
                      <>
                        <i className="ri-money-dollar-circle-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                        Bayar Cash
                      </>
                    )}
                  </Button>
                )}

                {paymentMethod === 'card' && (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleCardPayment}
                    disabled={isProcessing}
                    className="w-full whitespace-nowrap cursor-pointer"
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line mr-2 w-5 h-5 flex items-center justify-center animate-spin"></i>
                        Memproses Kartu...
                      </>
                    ) : (
                      <>
                        <i className="ri-bank-card-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                        Bayar dengan Kartu
                      </>
                    )}
                  </Button>
                )}

                {paymentMethod === 'qris' && (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleQRISPayment}
                    disabled={isProcessing}
                    className="w-full whitespace-nowrap cursor-pointer"
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line mr-2 w-5 h-5 flex items-center justify-center animate-spin"></i>
                        Memproses QRIS...
                      </>
                    ) : (
                      <>
                        <i className="ri-qr-code-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                        Bayar dengan QRIS
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          ) : (
            /* Payment Completed - Show Invoice Options */
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-white text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h4 className="text-2xl font-bold text-green-400 mb-2">Pembayaran Berhasil!</h4>
                <p className="text-gray-300">Transaksi telah selesai diproses</p>
              </div>

              {/* Payment Details - Diperbaiki */}
              <div className="bg-green-900/20 rounded-2xl p-4 border border-green-500/30">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-300">No. Struk:</span>
                    <span className="text-white font-bold">{paymentData?.receiptNumber || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Metode:</span>
                    <span className="text-white font-semibold">
                      {paymentData?.method === 'cash' ? 'Cash' : 
                       paymentData?.method === 'card' ? 'Kartu' : 'QRIS'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Total:</span>
                    <span className="text-green-400 font-bold">Rp {(paymentData?.amount || 0).toLocaleString('id-ID')}</span>
                  </div>
                  {paymentData?.method === 'cash' && paymentData.received && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Diterima:</span>
                        <span className="text-white">Rp {paymentData.received.toLocaleString('id-ID')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Kembalian:</span>
                        <span className="text-yellow-400 font-bold">Rp {(paymentData.change || 0).toLocaleString('id-ID')}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={printInvoice}
                  className="w-full whitespace-nowrap cursor-pointer border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <i className="ri-printer-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Print Invoice
                </Button>
                
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleClosePaymentModal}
                  className="w-full whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-check-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                  Tutup
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
